/**
 * Synchronous single-flight guard for async form submit handlers.
 *
 * WHY THIS EXISTS (measured, not hypothetical)
 * On web-stg (bundle main.eb277bf7, 2026-07-31) three rapid clicks on the Log In
 * button produced THREE `POST /api/v1/login` requests. The button already carried
 * `disabled={isLoggingIn || ...}`, so a guard *looked* present — but
 * `setIsLoggingIn(true)` ran only AFTER two awaits (reCAPTCHA execution and the
 * notification-permission request). Every click landing inside that async window
 * saw a still-enabled button and started another login.
 *
 * The lesson generalises past login (G-114): a React state update is asynchronous
 * and the re-render it schedules cannot land before the next click in the same
 * tick, so state can never be the re-entrancy guard for an async handler. The
 * guard must be a plain mutable ref written SYNCHRONOUSLY, before the first await.
 *
 * Shaped as acquire/release rather than a `runGuarded(fn)` wrapper on purpose:
 * the real handler finishes inside react-query's `onSuccess`/`onError` callbacks,
 * which settle AFTER the submit function's own promise. A wrapper that released
 * in a `finally` would unlock the form while the login was still in flight —
 * i.e. it would not actually fix the measured defect.
 *
 * One behaviour, one file, one matching test file, so a failure names the fault.
 */

/**
 * @param {{current: boolean}} ref  a mutable ref (React `useRef(false)`) owned by
 *   the caller, so the guard survives re-renders.
 * @returns {{tryAcquire: () => boolean, release: () => void, isBusy: () => boolean}}
 *
 * `tryAcquire()` returns false when a submit is already in flight — the caller
 * must return immediately. It sets the flag synchronously, with no await before
 * it, which is the entire point.
 *
 * EVERY terminal path must call `release()`, including error and early-return
 * paths. A guard that leaks on the error path deadlocks the form after one failed
 * attempt — strictly worse than the double-submit it was meant to prevent.
 */
export function createSubmitGuard(ref) {
  return {
    tryAcquire() {
      if (ref.current) return false;
      ref.current = true;
      return true;
    },
    release() {
      ref.current = false;
    },
    isBusy() {
      return ref.current === true;
    },
  };
}

export default createSubmitGuard;
